import React from "react";
import { View, Text, StyleSheet, Platform } from "react-native";
import { useDispatch, useSelector } from "react-redux";
import LHModalPop from "../../../components/LHModalPop";
import { RootState } from "../../../store";
import {
  setErrorModalVisible,
  clearCentralizedErrors,
  selectCentralizedErrors,
  selectIsErrorModalVisible,
} from "../../../store/slices/leaderShipWalkSlice";

/**
 * Convert API name to user-friendly title format
 * Example: 'fetchZones' -> 'Fetch Zones Failed'
 */
const getApiTitle = (apiName: string): string => {
  // Convert camelCase to words and capitalize
  const words = apiName
    .replace(/([A-Z])/g, ' $1') // Add space before capitals
    .replace(/^./, str => str.toUpperCase()) // Capitalize first letter
    .trim();
  
  return `${words} Failed`;
};

/**
 * Centralized Error Modal for Leadership Walk Operations
 * 
 * This component displays API errors in a user-friendly way using the LHModalPop component.
 * It automatically appears when an API error occurs and provides options to:
 * - Dismiss the error
 * 
 * Features:
 * - Consistent error display across all Leadership Walk components
 * - User-friendly error messages
 * - Error tracking and logging
 * - Responsive design
 */
const LeadershipWalkErrorModal: React.FC = () => {
  const dispatch = useDispatch();
  const errors = useSelector(selectCentralizedErrors);
  const isVisible = useSelector(selectIsErrorModalVisible);

  // Get the most recent error
  const activeError = errors.length > 0 ? errors[errors.length - 1] : null;

  const handleClose = () => {
    dispatch(setErrorModalVisible(false));
    dispatch(clearCentralizedErrors());
  };

  if (!activeError) {
    return null;
  }

  return (
    <LHModalPop
      visible={isVisible}
      onClose={handleClose}
      title={getApiTitle(activeError.apiName)}
      description={activeError.message}
      showFooter={true}
      primaryButtonText="OK"
      secondaryButtonText=""
      onPrimaryPress={handleClose}
      showCloseButton={true}
    />
  );
};

const styles = StyleSheet.create({
  modalMessage: {
    fontSize: 16,
    color: '#3A3A3A',
    lineHeight: 24,
    textAlign: 'center',
    marginVertical: 10,
    fontFamily: Platform.select({
      ios: 'Inter',
      android: 'Inter-Regular',
      web: 'Inter, -apple-system, Roboto, Helvetica, sans-serif',
    }),
  },
});

export default LeadershipWalkErrorModal;
